

import React, { useState, useEffect } from 'react';
import { 
  Menu, X, Home, PlusCircle, FileStack, 
  LogOut, Shield, Database, CheckCircle2, 
  Sparkles, LockKeyhole
} from 'lucide-react';
// Corrected import path for types.ts
import { User, UserRole, ViewState } from '@/types'; // Corrected import path
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { DEFAULT_CONFIG } from '@/config'; // Import DEFAULT_CONFIG

interface LayoutProps {
  currentUser: User;
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  onLogout: () => void;
  children: React.ReactNode;
  appLogo?: string;
  appName?: string; // Add appName prop
  pendingApprovalCount?: number;
  settingsUpdatedAt?: number; // New: Timestamp for settings update
}

export const Layout: React.FC<LayoutProps> = ({ 
  currentUser, 
  currentView, 
  onNavigate, 
  onLogout, 
  children,
  appLogo,
  appName, // Destructure appName
  pendingApprovalCount = 0,
  settingsUpdatedAt // Destructure new prop
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  
  // REMOVIDO: logoLoadError e useEffect para resetar o erro.
  // A `key` no `<img>` com `settingsUpdatedAt` vai forçar a re-renderização.

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  
  const handleNavigate = (view: ViewState) => {
    onNavigate(view);
    setIsMobileMenuOpen(false);
  };

  const isAdmin = currentUser.role === UserRole.ADMIN;
  const canPublish = currentUser.role === UserRole.PUBLISHER || isAdmin;

  // Sidebar Items
  const renderNavItems = () => (
    <>
      <button
        onClick={() => handleNavigate('dashboard')}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${
          (currentView === 'dashboard' || currentView === 'article-view')
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/20' 
            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
        }`}
      >
        <Home size={20} className={(currentView === 'dashboard' || currentView === 'article-view') ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
        <span className="font-medium">Início</span>
      </button>

      <button
        onClick={() => handleNavigate('chat')}
        className={`w-full flex items-start gap-3 px-4 py-3 rounded-lg transition-all group text-left ${
          currentView === 'chat' 
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/20' 
            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
        }`}
      >
        <Sparkles size={22} className={`flex-shrink-0 mt-0.5 ${currentView === 'chat' ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
        <div className="flex flex-col">
          <span className="font-bold text-[15px] leading-tight">Soes Brain</span>
          <span className={`text-[11px] mt-1.5 font-normal leading-tight opacity-90 ${currentView === 'chat' ? 'text-blue-100' : 'text-slate-500 group-hover:text-slate-400'}`}>
            Inteligência Pro Ativa.
          </span>
        </div>
      </button>

      {canPublish && (
        <>
          <div className="my-4 border-t border-slate-800/50 mx-4"></div>
          <p className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Publicação</p>
          
          <button
            onClick={() => handleNavigate('submit')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${
              currentView === 'submit' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/20' 
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <PlusCircle size={20} className={currentView === 'submit' ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
            <span className="font-medium">Publicar</span>
          </button>

          <button
            onClick={() => handleNavigate('my-articles')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${
              currentView === 'my-articles' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/20' 
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <FileStack size={20} className={currentView === 'my-articles' ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
            <span className="font-medium">Meus Conhecimentos</span>
          </button>
        </>
      )}

      {isAdmin && (
        <>
          <div className="my-4 border-t border-slate-800/50 mx-4"></div>
          <p className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Administração</p>
          
          <button
            onClick={() => handleNavigate('approvals')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group relative ${
              currentView === 'approvals' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/20' 
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <CheckCircle2 size={20} className={currentView === 'approvals' ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
            <span className="font-medium">Aprovações</span>
            {pendingApprovalCount > 0 && (
              <span className="absolute right-4 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {pendingApprovalCount}
              </span>
            )}
          </button>

          <button
            onClick={() => handleNavigate('admin')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${
              currentView === 'admin' 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/20' 
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Shield size={20} className={currentView === 'admin' ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
            <span className="font-medium">Painel Admin</span>
          </button>

        </>
      )}
    </>
  );

  const currentLogoUrl = appLogo || DEFAULT_CONFIG.logoUrl;
  const displayAppName = (appName || DEFAULT_CONFIG.appName || 'SÓES Wiki').replace(/s[óo]\s+escrit[óo]rio/gi, 'SÓES'); // Added for fallback text

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 text-white flex items-center justify-between px-4 z-40 shadow-md">
         <div className="flex items-center gap-3">
            {currentLogoUrl ? 
              <img 
                key={`mobile-logo-${currentLogoUrl}-${settingsUpdatedAt || DEFAULT_CONFIG.lastUpdatedAt}`} // Key agora inclui timestamp de atualização
                src={currentLogoUrl} 
                alt="Logo" 
                className="h-10 w-auto object-contain" 
                onError={(e) => { 
                  console.error('Layout: Mobile logo failed to load:', currentLogoUrl, e); 
                  // REMOVIDO: e.currentTarget.style.display = 'none';
                }}
              /> 
              : <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-white text-xs">SÓES</div>
            }
            <span className="font-bold text-lg">{displayAppName}</span>
         </div>
         <button onClick={toggleMobileMenu} className="p-2 text-slate-300">
           {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
         </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30 w-72 bg-slate-900 text-slate-300 flex flex-col transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:h-screen shadow-xl lg:shadow-none
      `}>
        <div className="h-24 flex items-center justify-center px-6 border-b border-slate-800/50 bg-slate-900">
          {currentLogoUrl ? 
            <img 
              key={`desktop-logo-${currentLogoUrl}-${settingsUpdatedAt || DEFAULT_CONFIG.lastUpdatedAt}`} // Key agora inclui timestamp de atualização
              src={currentLogoUrl} 
              alt="Logo" 
              className="max-h-16 max-w-full w-auto object-contain" 
              onError={(e) => { 
                console.error('Layout: Desktop logo failed to load:', currentLogoUrl, e); 
                // REMOVIDO: e.currentTarget.style.display = 'none';
              }}
            /> 
            : <div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-white text-sm shadow-lg">SÓES</div><h1 className="font-bold text-xl text-white tracking-tight">{displayAppName}</h1></div>
          }
        </div>

        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1 scrollbar-thin scrollbar-thumb-slate-700">
          {renderNavItems()}
        </nav>

        {/* User Profile Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-slate-700 overflow-hidden border border-slate-600">
               <img src={currentUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.fullName)}&background=random`} alt="User" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{currentUser.fullName}</p>
              <p className="text-xs text-slate-500 truncate">{currentUser.role}</p>
            </div>
          </div>
          
          <div className="flex gap-2">
             <button onClick={() => setIsPasswordModalOpen(true)} className="flex-1 flex items-center justify-center gap-2 p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-300 transition-colors"><LockKeyhole size={14} /> Senha</button>
             <button 
                onClick={(e) => {
                  e.preventDefault();
                  onLogout();
                }} 
                className="flex-1 flex items-center justify-center gap-2 p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs transition-colors"
             >
               <LogOut size={14} /> Sair
             </button>
          </div>
        </div>
      </aside>

      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />}

      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden pt-16 lg:pt-0">
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 md:p-8 relative">
           {children}
        </div>
      </main>
      
      {isPasswordModalOpen && <ChangePasswordModal onClose={() => setIsPasswordModalOpen(false)} />}
    </div>
  );
};